import moment from 'moment';
import * as R from 'ramda';
import uuid from 'uuid/v1';
import { Twix } from './Twix';
import { initConfig } from './apiConfig';
import { now } from '../../utils';
import * as Promise from 'bluebird';

const stringify = (data, prefix) => {
  const obj = JSON.parse(JSON.stringify(data));

  const serialize = R.mapObjIndexed((v, p) => {
    const k = prefix ? prefix + '[' + p + ']' : p;
    return v !== null && typeof v === 'object' ? stringify(v, k) : encodeURIComponent(k) + '=' + encodeURIComponent(v);
  });

  return R.pipe(
    serialize,
    R.values,
    R.join('&')
  )(obj);
};

const sessionHandler = {
  accessToken: null,
  userData: null,
  autologinCredentials: {},
  checkOfflineOn: false,
  expireAt: 0,
  statusLastCallTime: 0,
  lastStatus: true,
  forceLocalSync: false,
};

const apiInit = apiUrl => globalOptions => {
  if (!apiUrl) {
    throw 'An API url has to be set';
  }

  const workerPool = {};
  const apiOptions = R.merge(
    {
      apiActions: { connectionStatus: status => console.log('api connection status', status) },
      fixedParams: {
        time_offset: new Date().getTimezoneOffset() * 60 * 1000,
      },
      fixedData: {},
    },
    globalOptions
  );

  const addFixedParam = (paramName, paramValue) => {
    apiOptions.fixedParams[paramName] = paramValue;
  };

  const addFixedData = (paramName, paramValue) => {
    apiOptions.fixedData[paramName] = paramValue;
  };

  const ajax = (path, options) =>
    new Promise((success, fail) => {
      const requestUUID = uuid();
      const isGetType = options.type != 'POST' && options.type != 'PUT' && options.type != 'DELETE';
      // const isGetType = options.type != 'POST' && options.type != 'DELETE';

      const params = R.pipe(
        R.merge(apiOptions.fixedParams),
        params => (isGetType ? R.merge(params)(options.data || {}) : params),
        stringify
      )(options.params);

      console.log('params:', params);

      const requestUrl = (options.url ? options.url : apiUrl) + path + '?' + params;
      const data = options.processData
        ? R.merge(apiOptions.fixedData, !isGetType ? options.data : {})
        : options.data || {};
      console.log('loading ' + requestUrl);

      const ajaxOptions = R.pipe(
        R.merge(R.omit('noWorker')(options)),
        R.omit('processData'),
        R.merge({ type: 'GET', uuid: requestUUID })
      )(
        options.noWorker
          ? {
              url: requestUrl,
              success,
              error: (_, __, errorThrown) => {
                fail(errorThrown);
              },
              data: data,
            }
          : {
              url: requestUrl,
              crossDomain: true,
              data: !isGetType && options.processData !== false ? stringify(data) : data,
              headers: !isGetType
                ? R.merge({
                    'content-type': 'application/x-www-form-urlencoded',
                  })(options.headers)
                : options.headers,
            }
      );

      if (options.noWorker) {
        Twix.ajax(ajaxOptions);
      } else {
        workerPool[requestUUID] = new Worker('api/apiWorker.js');

        workerPool[requestUUID].onmessage = event => {
          const response = event.data;
          // console.log('response', response);
          workerPool[response.uuid].terminate();

          if (response.type == 'success') {
            const data = response.data;
            success(data);
          } else if (response.type == 'error') {
            if (response.errorThrown === 401) {
              fail('Session expired');
            } else {
              fail(response.errorThrown);
            }
          }
        };

        // console.log('request', ajaxOptions);
        workerPool[requestUUID].postMessage(ajaxOptions);
      }
    });

  const getApiUrl = path => {
    const params = R.pipe(
      R.merge(apiOptions.fixedParams),
      stringify
    )({});

    console.log('params:', params);

    return apiUrl + path + '?' + params;
  };

  const setAccessToken = ({ access_token, user_data, autologinCredentials, checkOfflineOn }) => {
    sessionHandler.accessToken = access_token;
    sessionHandler.userData = user_data;
    sessionHandler.autologinCredentials = autologinCredentials;
    sessionHandler.checkOfflineOn = checkOfflineOn || false;
    if (access_token && access_token.expires_in) {
      addFixedParam('access_token', access_token.access_token);
      sessionHandler.expireAt = now() + access_token.expires_in * 1;
    } else {
      sessionHandler.expireAt = 0;
    }
    console.log(sessionHandler);
  };

  const checkAccessToken = () =>
    new Promise((success, fail) => {
      console.log('Checking session');
      console.log('Session expires At', sessionHandler.expireAt);
      if (now() > sessionHandler.expireAt) {
        console.log('Session expired');
        const DAY_SPAN = 60 * 60 * 24 * 1000;
        if (sessionHandler.accessToken && now() - sessionHandler.expireAt < DAY_SPAN) {
          console.log('Refreshing session');
          ajax(`user/refreshToken/${sessionHandler.accessToken.refresh_token}.json`, { type: 'GET' })
            .then(access_token => {
              sessionHandler.accessToken = access_token;
              addFixedParam('access_token', access_token.access_token);
              success();
            })
            .catch(fail);
        } else {
          console.log('autoLoging User');
          const { username, password } = sessionHandler.autologinCredentials;

          ajax(`user/login`, {
            type: 'POST',
            data: {
              username,
              password,
            },
          })
            .then(access_token => {
              setAccessToken({
                access_token,
                autologinCredentials: sessionHandler.autologinCredentials,
                checkOfflineOn: sessionHandler.checkOfflineOn,
              });
              addFixedParam('access_token', access_token.access_token);
              success();
            })
            .catch(fail);
        }
      } else {
        console.log('Session ok');
        success();
      }
    });

  const ajaxSession = (path, options) =>
    new Promise((success, fail) => {
      checkOffline()
        .then(offline => {
          if (!offline) {
            console.log('online');
            R.pipeP(
              checkAccessToken,
              () => {
                console.log('Sending request');
                return ajax(path, options);
              }
            )()
              .then(success)
              .catch(fail);
          } else {
            console.log('offline');
            fail('offline');
          }
        })
        .catch(() => {
          console.log('offline');
          fail('offline');
        });
    });

  const getSessionApiUrl = path =>
    new Promise((success, fail) => {
      checkOffline()
        .then(offline => {
          if (!offline) {
            console.log('online');
            R.pipeP(
              checkAccessToken,
              () => {
                console.log('Getting API url');
                return Promise.resolve(getApiUrl(path));
              }
            )()
              .then(success)
              .catch(fail);
          } else {
            console.log('offline');
            fail('offline');
          }
        })
        .catch(() => {
          console.log('offline');
          fail('offline');
        });
    });

  const getUserSessionUsername = () => sessionHandler.autologinCredentials.username;
  const getUserSessionUserData = () => sessionHandler.userData;
  const setUserSessionUserData = user_data => (sessionHandler.userData = user_data);
  const getUserSessionForceLocalSync = () => sessionHandler.forceLocalSync;
  const setUserSessionForceLocalSync = forceLocalSync => (sessionHandler.forceLocalSync = forceLocalSync);

  const checkOffline = () =>
    new Promise(success => {
      console.log('Checking Offline');
      if (sessionHandler.checkOfflineOn) {
        const statusNow = moment();

        if (sessionHandler.statusLastCallTime === 0 || statusNow.diff(sessionHandler.statusLastCallTime) > 60 * 1000) {
          console.log('Checking Status');
          Twix.ajax({
            url: globalOptions.appUrl + 'app/status',
            success: ({ status }) => {
              sessionHandler.lastStatus = status !== 'online';
              sessionHandler.statusLastCallTime = moment();
              console.log('Server Offline', sessionHandler.lastStatus);
              apiOptions.apiActions.connectionStatus({
                connected: !sessionHandler.lastStatus,
                state: 'online',
                error: '',
              });
              success(sessionHandler.lastStatus);
            },
            error: error => {
              console.log('NO CONNECTION OR ERROR', error);
              sessionHandler.lastStatus = true;
              sessionHandler.statusLastCallTime = moment();
              console.log(`Status error ${error} / Offline`, sessionHandler.lastStatus);
              apiOptions.apiActions.connectionStatus({
                connected: !sessionHandler.lastStatus,
                state: 'server error',
                error,
              });
              success(sessionHandler.lastStatus);
            },
          });
        } else {
          console.log('Cached offline', sessionHandler.lastStatus);
          apiOptions.apiActions.connectionStatus({
            connected: !sessionHandler.lastStatus,
            offline: 'cached',
            error: '',
          });
          success(sessionHandler.lastStatus);
        }
      } else {
        console.log('CheckofflineOn off');
        apiOptions.apiActions.connectionStatus({
          connected: !sessionHandler.lastStatus,
          offline: 'offline mode',
          error: '',
        });
        success(true);
      }
    });

  const setApiActions = actions => (apiOptions.apiActions = R.merge(apiOptions.apiActions, actions));

  return {
    addFixedParam,
    addFixedData,
    ajax,
    ajaxSession,
    checkOffline,
    setAccessToken,
    getUserSessionUsername,
    getUserSessionUserData,
    setUserSessionUserData,
    getUserSessionForceLocalSync,
    setUserSessionForceLocalSync,
    getSessionApiUrl,
    setApiActions,
  };
};

export const apiConfig = initConfig(process.env.API_CONFIG_KEY);
export const api = apiInit(apiConfig.apiServer)({ appUrl: apiConfig.appUrl });
